import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { track, type Surface } from './telemetry';
import { prefixMessage, type ToastMessage } from './useToasts';
import { useAccountStorage } from './account/context';
import type { KeyValueStorage } from './account/storage';
import { isAccountPath } from '../features/account/paths';

/**
 * The user's two lists, held in localStorage, with undo/redo.
 *
 *   schedule — "Add to Schedule". Full opacity on My Schedule.
 *   saved    — "Save for Later". 50% opacity on My Schedule.
 *
 * The lists are mutually exclusive: adding an event to the schedule removes it
 * from Save for Later, so an event appears in exactly one place. Saving an
 * event that is already scheduled moves it the other way, which keeps both
 * buttons behaving like plain toggles.
 *
 * Keys are the enrichment pass's stable `id`, not the title — 6 of 997 titles
 * collide in the scrape.
 *
 * History is a past/future stack over whole snapshots rather than an inverse
 * operation per action. Snapshots are two small arrays of ids, so the memory
 * cost is trivial, and it means a future action type (bulk clear, import) gets
 * undo for free instead of needing its own inverse.
 */

const SCHEDULE_KEY = 'mcw.schedule';
const SAVED_KEY = 'mcw.saved';
const HISTORY_LIMIT = 50;

interface Snapshot {
  scheduled: string[];
  saved: string[];
}

interface HistoryState {
  past: { snapshot: Snapshot; label: ToastMessage }[];
  present: Snapshot;
  future: { snapshot: Snapshot; label: ToastMessage }[];
  /** What the last undo/redo did, for the transient toast. */
  announcement: { message: ToastMessage; at: number } | null;
}

type Action =
  | { type: 'toggleScheduled'; id: string; title: string }
  | { type: 'toggleSaved'; id: string; title: string }
  | { type: 'clear'; scope: 'all' | 'scheduled' }
  | { type: 'undo' }
  | { type: 'redo' }
  /** The lists moved (into the account, or a newer copy of it). */
  | { type: 'reset'; present: Snapshot };

function read(key: string, storage: KeyValueStorage): string[] {
  try {
    const raw = storage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function write(key: string, ids: string[], storage: KeyValueStorage): void {
  try {
    storage.setItem(key, JSON.stringify(ids));
  } catch {
    // Site data blocked. The lists still work for this session.
  }
}

function commit(state: HistoryState, next: Snapshot, label: ToastMessage): HistoryState {
  return {
    past: [...state.past, { snapshot: state.present, label }].slice(-HISTORY_LIMIT),
    present: next,
    future: [],
    announcement: null,
  };
}

function reducer(state: HistoryState, action: Action): HistoryState {
  switch (action.type) {
    case 'toggleScheduled': {
      const { id, title } = action;
      const had = state.present.scheduled.includes(id);
      return commit(
        state,
        {
          scheduled: had
            ? state.present.scheduled.filter((x) => x !== id)
            : [...state.present.scheduled, id],
          // Promoting to the schedule takes it out of Save for Later.
          saved: state.present.saved.filter((x) => x !== id),
        },
        had
          ? { before: 'removed “', title, after: '” from your schedule' }
          : { before: 'added “', title, after: '” to your schedule' },
      );
    }

    case 'toggleSaved': {
      const { id, title } = action;
      const had = state.present.saved.includes(id);
      return commit(
        state,
        {
          saved: had ? state.present.saved.filter((x) => x !== id) : [...state.present.saved, id],
          scheduled: state.present.scheduled.filter((x) => x !== id),
        },
        had
          ? { before: 'removed “', title, after: '” from Save for Later' }
          : { before: 'saved “', title, after: '” for later' },
      );
    }

    case 'clear': {
      const count =
        action.scope === 'all'
          ? state.present.scheduled.length + state.present.saved.length
          : state.present.scheduled.length;
      return commit(
        state,
        {
          scheduled: [],
          saved: action.scope === 'all' ? [] : state.present.saved,
        },
        `cleared ${count} ${count === 1 ? 'event' : 'events'}`,
      );
    }

    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        past: state.past.slice(0, -1),
        present: previous.snapshot,
        future: [{ snapshot: state.present, label: previous.label }, ...state.future].slice(0, HISTORY_LIMIT),
        announcement: { message: prefixMessage('Undid: ', previous.label), at: Date.now() },
      };
    }

    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      return {
        past: [...state.past, { snapshot: state.present, label: next.label }].slice(-HISTORY_LIMIT),
        present: next.snapshot,
        future: state.future.slice(1),
        announcement: { message: prefixMessage('Redid: ', next.label), at: Date.now() },
      };
    }

    // History goes too: undoing to a snapshot from the other place would undo
    // things this view never did.
    case 'reset':
      return { past: [], present: action.present, future: [], announcement: null };

    default:
      return state;
  }
}

export interface Collections {
  scheduled: Set<string>;
  saved: Set<string>;
  isScheduled: (id: string) => boolean;
  isSaved: (id: string) => boolean;
  toggleScheduled: (id: string, from: Surface, title?: string) => void;
  toggleSaved: (id: string, from: Surface, title?: string) => void;
  /** True when the id is on a list, so callers can word their own message. */
  wasScheduled: (id: string) => boolean;
  clear: (scope: 'all' | 'scheduled') => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  announcement: { message: ToastMessage; at: number } | null;
}

/** Don't steal ⌘Z from a field where the browser's own text undo is what's wanted. */
function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function useCollections(): Collections {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    past: [],
    present: { scheduled: read(SCHEDULE_KEY, storage), saved: read(SAVED_KEY, storage) },
    future: [],
    announcement: null,
  }));

  // The lists moved: start again from wherever they now live.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    dispatch({ type: 'reset', present: { scheduled: read(SCHEDULE_KEY, storage), saved: read(SAVED_KEY, storage) } });
  }

  useEffect(() => write(SCHEDULE_KEY, state.present.scheduled, storage), [state.present.scheduled, storage]);
  useEffect(() => write(SAVED_KEY, state.present.saved, storage), [state.present.saved, storage]);

  const scheduled = useMemo(() => new Set(state.present.scheduled), [state.present.scheduled]);
  const saved = useMemo(() => new Set(state.present.saved), [state.present.saved]);

  const toggleScheduled = useCallback(
    (id: string, from: Surface, title = 'this event') => {
      track({ t: scheduled.has(id) ? 'schedule_remove' : 'schedule_add', id, from });
      dispatch({ type: 'toggleScheduled', id, title });
    },
    [scheduled],
  );

  const toggleSaved = useCallback(
    (id: string, from: Surface, title = 'this event') => {
      track({ t: saved.has(id) ? 'save_remove' : 'save_add', id, from });
      dispatch({ type: 'toggleSaved', id, title });
    },
    [saved],
  );

  const clear = useCallback((scope: 'all' | 'scheduled') => dispatch({ type: 'clear', scope }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);

  // ⌘/Ctrl+Z to undo; ⌘/Ctrl+Y or ⌘/Ctrl+Shift+Z to redo.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (isTextEntry(e.target)) return;
      // The account page is drawn over the app, which stays mounted under it:
      // ⌘Z there is not an undo of anything the reader can see.
      if (isAccountPath(window.location.pathname)) return;

      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'undo' });
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        dispatch({ type: 'redo' });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return useMemo(
    () => ({
      scheduled,
      saved,
      isScheduled: (id: string) => scheduled.has(id),
      isSaved: (id: string) => saved.has(id),
      wasScheduled: (id: string) => scheduled.has(id),
      toggleScheduled,
      toggleSaved,
      clear,
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      announcement: state.announcement,
    }),
    [scheduled, saved, toggleScheduled, toggleSaved, clear, undo, redo, state.past.length, state.future.length, state.announcement],
  );
}
